import {
    alpha,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    Collapse,
    Divider,
    Grid,
    IconButton,
    Popover,
    Skeleton,
    Stack,
    Typography
} from "@mui/material";
import React from "react";
import ArrowDropDownCircleOutlinedIcon from "@mui/icons-material/ArrowDropDownCircleOutlined";
import PropTypes from "prop-types";
import InfoLabel from "./InfoLabel";
import {useTranslation} from "react-i18next";

export function SelectLabelSetSkeleton() {
    return (
        <Card sx={{width: "100%", userSelect: "none"}} elevation={0}>
            <CardHeader
                title={
                    <Skeleton width="150px"/>
                }
                subheader={
                    <Skeleton width="200px"/>
                }
                action={
                    <Checkbox
                        edge="end"
                        checked={true}
                        disabled
                    />
                }/>
            <CardContent/>
        </Card>
    );
}

SelectLabelSet.propTypes = {
    element: PropTypes.shape({
            name: PropTypes.string.isRequired,
            description: PropTypes.string,
            labels: PropTypes.array.isRequired
        }
    ),
    checked: PropTypes.bool,
    onCheck: PropTypes.func
}

SelectLabelSet.defaultProps = {
    checked: false,
    onCheck: () => {
    }
}

export function SelectLabelSet({element, checked, onCheck}) {
    const {t} = useTranslation('common');
    const [displayLabels, setDisplayLabels] = React.useState(false);
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [currentLabel, setCurrentLabel] = React.useState(undefined);

    const handleClick = (event, label) => {
        setAnchorEl(event.currentTarget);
        setCurrentLabel(label);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? `chip-popover-${currentLabel.id}` : undefined;

    const handleToggle = () => {
        onCheck();
    }

    if (element) return (
        <Card sx={{width: "100%", userSelect: "none"}} elevation={0}>
            <CardHeader
                title={element.name}
                subheader={element.description}
                titleTypographyProps={{variant: "h6"}}
                action={
                    <Checkbox
                        edge="end"
                        onChange={handleToggle}
                        checked={checked}
                    />
                }/>
            <CardContent sx={{m: 1, p: 0}}>
                <Stack direction="row" spacing={1} width="100%" alignItems="center">
                    <Typography variant="caption" fontWeight="bold" color="textSecondary">
                        {t("main.components.annotator.labels.title")}
                    </Typography>
                    <Divider sx={{flexGrow: 1}}/>
                    <IconButton onClick={() => setDisplayLabels(prevState => !prevState)}>
                        <ArrowDropDownCircleOutlinedIcon
                            sx={{transform: displayLabels ? "rotate(180deg)" : undefined}}/>
                    </IconButton>
                </Stack>
                <Collapse in={displayLabels}>
                    <Grid container justifyContent={"space-evenly"} spacing={2}>
                        {element.labels.map((label) => (
                            <Grid item key={`select-label-${label.id}`}>
                                <Chip
                                    sx={{
                                        "&.MuiChip-root": {
                                            color: theme => theme.palette.getContrastText(label.color),
                                            bgcolor: label.color ? label.color : "default",
                                            ":hover": {
                                                bgcolor: label.color ? alpha(label.color, 0.6) : "default",
                                            }
                                        }
                                    }}
                                    label={label.label}
                                    onClick={(event) => handleClick(event, label)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </Collapse>
            </CardContent>
            <Popover
                id={id}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <InfoLabel label={currentLabel}/>
            </Popover>
        </Card>
    );

    return <SelectLabelSetSkeleton/>
}